import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { ENV } from '../config/constants';
import { ErrorBoundaryTrack } from './ErrorBoundaryTrack';

const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';
const CSS_CHUNK_LOAD_ERROR_CODE = 'CSS_CHUNK_LOAD_FAILED';

const errorIsChunkLoadError = (error) => {
  const { code, name, constructor } = error;

  if (name === CHUNK_LOAD_ERROR_NAME || code === CSS_CHUNK_LOAD_ERROR_CODE) return true;
  if (
    constructor &&
    (constructor.name === CHUNK_LOAD_ERROR_NAME || constructor.code === CSS_CHUNK_LOAD_ERROR_CODE)
  ) {
    return true;
  }

  return false;
};

class ErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      isReloading: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Our deploys currently delete the previous version's code chunks from
    // s3, resulting in a situation where a user already on the site during
    // the deploy could end up lazy-loading a chunk that no longer exists.
    // A full, cache-less window reload should be able to force the user
    // into downloading the latest assets.
    const isChunkLoadError = errorIsChunkLoadError(error);
    if (isChunkLoadError) window.location.reload(true);

    Sentry.captureException(error);
    this.setState({
      error,
      isReloading: isChunkLoadError,
    });
  }

  render() {
    const { error, isReloading } = this.state;

    if (isReloading) return <div></div>;

    if (error) {
      return (
        <div style={{ padding: 24 }}>
          <h1>Sorry!</h1>
          <p>There was an error loading this page. Please try again.</p>
          {ENV !== 'production' && <pre>{error.stack}</pre>}
          <ErrorBoundaryTrack error={error} />
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
